/**
 * A picture fetched only while it can be seen (TMMAPP-0142 K1): the one loader for the transcript's
 * pictures and the help library's images. Both need the bearer token, so each is read as a blob
 * through the API client and shown by an object URL.
 *
 * - The element is watched with an `IntersectionObserver` (with a margin, so a picture is ready just
 *   before it scrolls in), rooted at the scroll area it sits in (`LazyRootContext`, M9). It loads
 *   when it comes into view.
 * - When it leaves the view, its URL is revoked and the blob dropped, unless `keep` (a viewer shows
 *   it), and a download still running is aborted (M8). It loads again if it comes back.
 * - Where the browser has no observer, the picture loads at once.
 * - A failed load stays failed: the caller shows its fallback (the file chip, the alt text).
 * - On unmount the URL is revoked. Nothing is stored in the browser.
 */
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { LazyRootContext } from './lazyRootContext';

/** How far outside the view a picture starts loading. */
export const LAZY_IMAGE_ROOT_MARGIN = '200px';

export interface LazyObjectUrl {
  /** A callback ref: attach to the element whose visibility decides the load. */
  observe: (node: Element | null) => void;
  /** The picture's object URL while it's held; null otherwise. */
  url: string | null;
  blob: Blob | null;
  failed: boolean;
}

function observable(): boolean {
  return typeof IntersectionObserver === 'function';
}

export function useLazyObjectUrl(
  load: (signal: AbortSignal) => Promise<Blob>,
  { keep = false }: { keep?: boolean } = {},
): LazyObjectUrl {
  const root = useContext(LazyRootContext);
  const [node, setNode] = useState<Element | null>(null);
  const [visible, setVisible] = useState(() => !observable());
  const [image, setImage] = useState<{ url: string; blob: Blob } | null>(null);
  const [failed, setFailed] = useState(false);
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  });

  const observe = useCallback((next: Element | null) => setNode(next), []);

  useEffect(() => {
    if (node === null || !observable()) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries.at(-1);
        if (entry !== undefined) setVisible(entry.isIntersecting);
      },
      { root, rootMargin: LAZY_IMAGE_ROOT_MARGIN },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, root]);

  const wanted = (visible || keep) && !failed;
  useEffect(() => {
    if (!wanted) return undefined;
    let live = true;
    let url: string | null = null;
    const abort = new AbortController();
    loadRef.current(abort.signal).then(
      (blob) => {
        if (!live) return;
        url = URL.createObjectURL(blob);
        setImage({ url, blob });
      },
      () => {
        if (live) setFailed(true);
      },
    );
    return () => {
      live = false;
      abort.abort();
      if (url !== null) URL.revokeObjectURL(url);
      setImage(null);
    };
  }, [wanted]);

  return {
    observe,
    url: wanted ? (image?.url ?? null) : null,
    blob: wanted ? (image?.blob ?? null) : null,
    failed,
  };
}
